import React, { useState, useEffect, useRef } from "react";
// REDUX
import { useSelector, useDispatch, useStore } from "react-redux";
import { updateFilters, updateBlockFilters } from "../../../redux/reports/reportsSlice";
import { usePostCarUseTimesMutation } from "../../../redux/reports/chartsAPI";
// COMPONENTS
import ReportsHeader from "../ReportsHeader";
import ChartBar from "../charts/ChartBar";
import ChartPie from "../charts/ChartPie";
import ChartLine from "../charts/ChartLine";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTotalCarsInBank, GenerateCarUseTimes } from "../utils";

const ReportCarUseChartTimes = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const store = useStore();

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [chartData, setChartData] = useState<any>([]);
	const skipFirstFiltersFetch = useRef(true);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const { theme } = useSelector((state: RootState) => state.home);
	const { filters, reportRefreshTable } = useSelector((state: RootState) => state.report);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetChart, { data, isLoading, isError, error }] = usePostCarUseTimesMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getChartData = async () => {
		const token = await GETJwtToken();
		triggerGetChart({
			token,
			body: {
				bank_id: filters.bankId,
				start_date: filters.startDate,
				end_date: filters.endDate,
				timezone: filters.timezoneCode,
				lift_type: "ELEVATOR",
			},
		});
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (data && data.results) {
			const response = GenerateCarUseTimes(data.results);
			setChartData(response);
		} else {
			setChartData([{ value: 0, tag: "", name: "" }]);
		}
	}, [data]);

	useEffect(() => {
		if (skipFirstFiltersFetch.current) {
			skipFirstFiltersFetch.current = false;
			return;
		}

		if (
			filters.bankId !== "" &&
			filters.startDate !== "" &&
			filters.endDate !== "" &&
			filters.timezoneCode !== "" &&
			filters.liftType === "ELEVATOR" &&
			!isLoading
		) {
			getChartData();
		}
	}, [filters.bankId, filters.startDate, filters.endDate, filters.timezoneCode, filters.liftType]);

	useEffect(() => {
		if (chartData && chartData.length > 0) {
			const response = getTotalCarsInBank(chartData);
			// Get the current state from Redux store to avoid stale closure values
			const currentFilters = (store.getState() as RootState).report.filters;
			dispatch(updateFilters({
				...currentFilters,
				selectedCarsList: response,
				carsList: response,
			}));
		}
	}, [chartData, dispatch, store]);

	useEffect(() => {
		dispatch(updateBlockFilters(isLoading));
		return () => { dispatch(updateBlockFilters(false)); };
	}, [isLoading]);

	useEffect(() => {
		if (reportRefreshTable) {
			getChartData();
		}
	}, [reportRefreshTable]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			{validatePermission("view-report-management-car-use-door-times", permissions) ? (
				<div className={`layout__contentPage__${theme}`}>
					<ReportsHeader
						reportDefaultData={{
							reportType: "CHART",
							levelTypeName: "BANKS",
							displaySelectTypeLevel: false,
							liftType: "ELEVATOR",
							defaultColumns: [],
							defaultOffsets: [],
							infoDescriptionText: "report.infoDescriptionText2",
							displayDownloadCSVReport: false,
							displayDownloadPDFReport: true,
							reportTitle: "Car Use / Door Times",
							displayAdvanceFiltersExclusions: false,
							displayAdvanceFiltersOffsets: false,
							disabledOffsets: true,
							useTablePagination: true,
							displayDateByRange: false,
							displayDate: true,
							displaySelectLiftType: false,
							displayAdvanceFilterCharts: true,
							displayAdvanceFiltersTable: false,
							displayRecentWeeksPicket: false,
							displayInputLimitItems: false,
							displayChartMirror: false,
							chartMirror: false,
							chartType: "bar_stack_vertical",
							dateTypeOptions: [{ id: "TODAY", name: "Day" }],
							dateDaysLimit: 0,
							displayMinimumActivation: false,
							dateWeeksLimit: 0,
							dateMonthsLimit: 0,
							dateQuarterLimit: 0,
							dateYearsLimit: 0,
							dateType: "TODAY",
							bankSearchAndFilterString: "&filter_target=type&filter=ELEVATOR",
						}}
					/>
					{filters.chartType === "pie" && <ChartPie charData={[]} />}
					{(filters.chartType === "line" ||
						filters.chartType === "line_natural" ||
						filters.chartType === "step_before" ||
						filters.chartType === "step_after") && (
							<ChartLine data={chartData} isLoading={isLoading} isError={isError} errorMessage={error && (error as any).data?.message} bottomLabel='CATEGORY' leftLabel='SECONDS' />
						)}
					{(filters.chartType === "bar_vertical" ||
						filters.chartType === "bar_stack_vertical" ||
						filters.chartType === "bar_horizontal" ||
						filters.chartType === "bar_stack_horizontal") && (
							<>
								<ChartBar data={chartData} isLoading={isLoading} isError={isError} errorMessage={error && (error as any).data?.message} bottomLabel='CATEGORY' leftLabel='SECONDS' />
							</>
						)}
				</div>
			) : (
				<UnauthorizedPage />
			)}
		</div>
	);
};

export default ReportCarUseChartTimes;
